import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Target, Shield, Trophy, User, Plus } from "lucide-react";
import { DartsEvent } from "./types";
import { eventsApi, getApiErrorMessage, toDE } from "./api";
import type { EventCreateRequest } from "./api/events";
import { useToast } from "./context/ToastContext";
import ToastContainer from "./components/ui/ToastContainer";
import SignInView from "./views/SignInView";
import SignUpView from "./views/SignUpView";
import DashboardView from "./views/DashboardView";
import TournamentsView from "./views/TournamentsView";
import TournamentBracketView from "./views/TournamentBracketView";
import RankingView from "./views/RankingView";
import ProfileView from "./views/ProfileView";
import EventDetailModal from "./components/modals/EventDetailModal";
import EventFormModal from "./components/modals/EventFormModal";
import { UI_TEXT } from "./constants/uiText";
import { MypageProvider } from "./context/MypageContext";
import { EventsProvider, useEventsContext } from "./context/EventsContext";
import { RankingsProvider } from "./context/RankingsContext";

const NAV_ITEMS = [
  { id: "home", path: "/home", icon: Target, label: UI_TEXT.nav.home },
  {
    id: "tournaments",
    path: "/tournaments",
    icon: Shield,
    label: UI_TEXT.nav.tournaments,
  },
  {
    id: "ranking",
    path: "/ranking",
    icon: Trophy,
    label: UI_TEXT.nav.ranking,
  },
  { id: "profile", path: "/profile", icon: User, label: UI_TEXT.nav.profile },
];

const NAV_ACTIVE_COLORS: Record<string, string> = {
  home: "text-rose-500",
  tournaments: "text-amber-500",
  ranking: "text-blue-500",
  profile: "text-emerald-500",
};

const NAV_ACTIVE_GLOW: Record<string, string> = {
  home: "drop-shadow-[0_0_8px_rgba(244,63,94,0.9)]",
  tournaments: "drop-shadow-[0_0_8px_rgba(245,158,11,0.9)]",
  ranking: "drop-shadow-[0_0_8px_rgba(59,130,246,0.9)]",
  profile: "drop-shadow-[0_0_8px_rgba(16,185,129,0.9)]",
};

function ProtectedRoute({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: React.ReactNode;
}) {
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppContent({
  isAuthenticated,
  isAdmin,
  onLogin,
  onLogout,
}: {
  isAuthenticated: boolean;
  isAdmin: boolean;
  onLogin: (adminStatus?: boolean) => void;
  onLogout: () => void;
}) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchEvents } = useEventsContext();
  const [selectedEvent, setSelectedEvent] = useState<DartsEvent | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<DartsEvent | null>(null);

  const handleCloseEventDetailModal = () => {
    setSelectedEvent(null);
    fetchEvents();
  };

  const handleCloseEventFormModal = () => {
    setIsFormOpen(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleLogin = (view: string, adminStatus = false) => {
    onLogin(adminStatus);
    navigate("/home");
  };

  const handleNotificationClick = () => {
    navigate("/notifications", { state: { from: location.pathname } });
  };

  const handleStageClick = (
    name: string,
    eventId: string,
    isEventEnded: boolean,
  ) => {
    navigate(`/tournaments/bracket/${eventId}`, {
      state: { tournamentName: name, isEventEnded },
    });
  };

  const handleViewTournamentResult = (event: DartsEvent) => {
    const tournamentName = [event.tournament, event.title]
      .filter((value) => !!value)
      .join(" ");
    setSelectedEvent(null);
    navigate(`/tournaments/bracket/${event.id}`, {
      state: { tournamentName, isEventEnded: true },
    });
  };

  const handleSubmitEvent = async (
    body: EventCreateRequest,
  ): Promise<boolean> => {
    try {
      if (editingEvent) {
        await eventsApi.update(editingEvent.id, body);
        if (selectedEvent?.id === editingEvent.id) {
          try {
            const rawEvents = await eventsApi.getAll();
            const refreshed = rawEvents.find((e) => e.id === editingEvent.id);
            if (refreshed) setSelectedEvent(toDE(refreshed));
          } catch {
            // 詳細モーダルの即時反映はベストエフォート。失敗時は次回再取得で同期される。
          }
        }
        showToast("イベントを更新しました。", "success");
      } else {
        await eventsApi.create(body);
        showToast("イベントを作成しました。", "success");
      }
      fetchEvents();
      setIsFormOpen(false);
      setEditingEvent(null);
      return true;
    } catch (e) {
      console.error("イベント登録 API エラー", e);
      showToast(
        editingEvent
          ? "イベントの更新に失敗しました。もう一度お試しください。"
          : "イベントの作成に失敗しました。もう一度お試しください。",
        "error",
      );
      return false;
    }
  };

  const currentPath = location.pathname;
  const isAuthPath = ["/login", "/signup"].includes(currentPath);
  const showNav = !isAuthPath && isAuthenticated;

  const activeNavId = currentPath.startsWith("/tournaments/bracket")
    ? "tournaments"
    : (NAV_ITEMS.find((item) => currentPath.startsWith(item.path))?.id ?? "");

  return (
    <div className="w-full h-full bg-[#f8fafc] font-sans selection:bg-rose-200 flex flex-col overflow-hidden relative">
      <style
        dangerouslySetInnerHTML={{
          __html: `.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }`,
        }}
      />
      <div className="flex-1 overflow-hidden relative">
        <Routes>
          <Route
            path="/"
            element={
              <Navigate to={isAuthenticated ? "/home" : "/login"} replace />
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <SignInView
                  onLogin={handleLogin}
                  onSwitchToSignUp={() => navigate("/signup")}
                />
              )
            }
          />
          <Route
            path="/signup"
            element={
              <SignUpView
                onLogin={handleLogin}
                onSwitchToLogin={() => navigate("/login")}
              />
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <DashboardView
                  onEventClick={setSelectedEvent}
                  onNotificationClick={handleNotificationClick}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Navigate to="/home" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournaments"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <TournamentsView
                  onStageClick={handleStageClick}
                  onNotificationClick={handleNotificationClick}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournaments/bracket/:eventId"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <BracketRouteWrapper
                  isAdmin={isAdmin}
                  onBack={() => navigate("/tournaments")}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ranking"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <RankingView onNotificationClick={handleNotificationClick} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <ProfileView
                  onLogout={() => {
                    onLogout();
                    navigate("/login");
                  }}
                  onNotificationClick={handleNotificationClick}
                />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>

      {/* 管理者用 FAB */}
      {isAdmin && currentPath === "/home" && (
        <button
          onClick={() => setIsFormOpen(true)}
          style={{ bottom: "calc(6.75rem + env(safe-area-inset-bottom))" }}
          className="fixed right-8 w-14 h-14 bg-rose-500 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-rose-600 active:scale-90 z-40 transition-all shadow-rose-500/30"
        >
          <Plus className="w-6 h-6" strokeWidth={3} />
        </button>
      )}

      {/* ボトムナビゲーション */}
      {showNav && (
        <div
          className="fixed left-0 bottom-0 w-full px-5 pt-2 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/90 to-transparent z-30 pointer-events-none"
          style={{
            paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
          }}
        >
          <div className="bg-slate-900 rounded-[2rem] shadow-[0_10px_40px_rgba(0,0,0,0.07)] border border-slate-200/50 px-4 py-1.5 flex justify-between items-center pointer-events-auto">
            {NAV_ITEMS.map((item) => {
              const isActive = item.id === activeNavId;
              const activeColor = isActive
                ? NAV_ACTIVE_COLORS[item.id]
                : "text-slate-400";
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className="flex flex-col items-center justify-center w-16 py-2 rounded-[1.5rem] transition-all"
                >
                  <item.icon
                    className={`w-[22px] h-[22px] mb-1 transition-all ${activeColor} ${isActive ? NAV_ACTIVE_GLOW[item.id] : ""}`}
                    strokeWidth={isActive ? 2.5 : 1.5}
                  />
                  <span className={`text-[9px] font-bold ${activeColor}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <EventDetailModal
        isOpen={!!selectedEvent}
        event={selectedEvent}
        onClose={handleCloseEventDetailModal}
        isAdmin={isAdmin}
        onViewTournamentResult={handleViewTournamentResult}
        onEdit={() => {
          setEditingEvent(selectedEvent);
          setIsFormOpen(true);
        }}
        onDelete={async (eventId) => {
          try {
            await eventsApi.remove(eventId);
            setSelectedEvent(null);
            setEditingEvent((current) =>
              current?.id === eventId ? null : current,
            );
            fetchEvents();
            showToast("イベントを削除しました。", "success");
          } catch (e) {
            console.error("イベント削除 API エラー", e);
            showToast(getApiErrorMessage(e), "error");
          }
        }}
        onStartTournament={async (eventId) => {
          try {
            await eventsApi.start(eventId);
            fetchEvents();
            showToast("イベントを開始しました。", "success");
            return true;
          } catch (e) {
            console.error("イベント開始 API エラー", e);
            showToast(getApiErrorMessage(e), "error");
            return false;
          }
        }}
        onEndTournament={async (eventId) => {
          try {
            await eventsApi.end(eventId);
            fetchEvents();
            showToast("イベントを終了しました。", "success");
          } catch (e) {
            console.error("イベント終了 API エラー", e);
            showToast(
              "イベントの終了に失敗しました。もう一度お試しください。",
              "error",
            );
          }
        }}
        onEntry={async (eventId) => {
          try {
            await eventsApi.entry(eventId);
            showToast("イベントに参加しました。", "success");
          } catch (e) {
            console.error("イベント参加 API エラー", e);
            showToast("参加に失敗しました。もう一度お試しください。", "error");
          }
        }}
        onCancelEntry={async (eventId) => {
          try {
            await eventsApi.cancelEntry(eventId);
            showToast("参加をキャンセルしました。", "success");
          } catch (e) {
            console.error("イベント参加キャンセル API エラー", e);
            showToast(
              "キャンセルに失敗しました。もう一度お試しください。",
              "error",
            );
          }
        }}
      />
      <EventFormModal
        isOpen={isFormOpen}
        onClose={handleCloseEventFormModal}
        onSubmit={handleSubmitEvent}
        isEdit={!!editingEvent}
        event={editingEvent}
      />
      <ToastContainer />
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // 起動時に localStorage から認証状態を復元する
  useEffect(() => {
    const token = localStorage.getItem("token");
    const isAdminStr = localStorage.getItem("isAdmin");
    if (token) {
      setIsAuthenticated(true);
      setIsAdmin(isAdminStr === "true");
    }
    setIsInitializing(false);
  }, []);

  // 初期化中は何も表示しない
  if (isInitializing) {
    return <div className="w-full min-h-viewport bg-[#f8fafc]" />;
  }

  return (
    <MypageProvider isAuthenticated={isAuthenticated}>
      <EventsProvider isAuthenticated={isAuthenticated}>
        <RankingsProvider isAuthenticated={isAuthenticated}>
          <AppContent
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            onLogin={(adminStatus = false) => {
              setIsAdmin(adminStatus);
              setIsAuthenticated(true);
            }}
            onLogout={() => setIsAuthenticated(false)}
          />
        </RankingsProvider>
      </EventsProvider>
    </MypageProvider>
  );
}

function BracketRouteWrapper({
  isAdmin,
  onBack,
}: {
  isAdmin: boolean;
  onBack: () => void;
}) {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const routeState = location.state as {
    tournamentName?: string;
    isEventEnded?: boolean;
  } | null;
  const tournamentName = routeState?.tournamentName ?? "";
  const isEventEnded = routeState?.isEventEnded ?? false;
  return (
    <TournamentBracketView
      tournamentName={tournamentName}
      eventId={eventId ?? ""}
      onBack={onBack}
      isAdmin={isAdmin}
      isEventEnded={!!isEventEnded}
    />
  );
}

export default App;
